import React, { useState, useEffect, useRef } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import './Garage.css';
import '../../ui/ArcadeShared.css';

// --- НОВЫЕ SVG ИКОНКИ ---

// ЛЕГКОВУШКА (V3): Еще уже, компактнее
const CarIcon = ({ style, className }) => (
  <svg viewBox="0 0 100 100" className={`garage-vehicle ${className}`} style={style}>
    {/* Колеса (Выступают по бокам) */}
    <rect x="22" y="15" width="10" height="18" fill="#1a1a1a" rx="2" />
    <rect x="68" y="15" width="10" height="18" fill="#1a1a1a" rx="2" />
    <rect x="22" y="67" width="10" height="18" fill="#1a1a1a" rx="2" />
    <rect x="68" y="67" width="10" height="18" fill="#1a1a1a" rx="2" />

    {/* Корпус (Сужен: x=32..68) ширина 36 (была 44) */}
    <path d="M32 10 L68 10 Q76 10 76 20 L76 80 Q76 90 68 90 L32 90 Q24 90 24 80 L24 20 Q24 10 32 10 Z" fill="currentColor" />

    {/* Лобовое стекло */}
    <path d="M28 28 L72 28 L68 40 L32 40 Z" fill="rgba(0,0,0,0.6)" />

    {/* Заднее стекло */}
    <path d="M32 75 L68 75 L66 65 L34 65 Z" fill="rgba(0,0,0,0.4)" />

    {/* Крыша */}
    <rect x="30" y="40" width="40" height="25" fill="rgba(255,255,255,0.15)" rx="2"/>
  </svg>
);

// ГРУЗОВИК: Широкий, квадратный
const TruckIcon = ({ style, className }) => (
  <svg viewBox="0 0 100 100" className={`garage-vehicle ${className}`} style={style}>
    {/* Кабина */}
    <rect x="20" y="5" width="60" height="25" rx="4" fill="currentColor" stroke="#111" strokeWidth="2" />
    <rect x="25" y="8" width="50" height="10" fill="rgba(0,0,0,0.7)" /> {/* Стекло */}

    {/* Кузов */}
    <rect x="10" y="35" width="80" height="60" rx="2" fill="#555" stroke="currentColor" strokeWidth="4" />
    {/* Ребра жесткости на крыше */}
    <line x1="10" y1="35" x2="90" y2="95" stroke="rgba(255,255,255,0.1)" strokeWidth="3"/>
    <line x1="90" y1="35" x2="10" y2="95" stroke="rgba(255,255,255,0.1)" strokeWidth="3"/>
  </svg>
);

// МОТОЦИКЛ: Очень узкий
const MotoIcon = ({ style, className }) => (
  <svg viewBox="0 0 100 100" className={`garage-vehicle ${className}`} style={style}>
    <rect x="30" y="20" width="40" height="4" fill="#ccc" /> {/* Руль */}
    <rect x="42" y="15" width="16" height="70" rx="8" fill="currentColor" /> {/* Корпус */}
    <rect x="45" y="5" width="10" height="15" fill="#111" /> {/* Переднее колесо */}
    <rect x="45" y="80" width="10" height="15" fill="#111" /> {/* Заднее колесо */}
    <circle cx="50" cy="55" r="9" fill="#111" /> {/* Седло */}
  </svg>
);

// АВТОБУС: Длинный
const BusIcon = ({ style, className }) => (
  <svg viewBox="0 0 100 100" className={`garage-vehicle ${className}`} style={style}>
    <rect x="22" y="2" width="56" height="96" rx="6" fill="currentColor" />
    <rect x="26" y="5" width="48" height="12" fill="rgba(0,0,0,0.7)" /> {/* Лобовое */}
    <rect x="32" y="25" width="36" height="60" fill="rgba(255,255,255,0.2)" rx="2" /> {/* Крыша */}
    <rect x="40" y="30" width="20" height="10" fill="rgba(0,0,0,0.3)" rx="1" /> {/* Люк */}
    <rect x="40" y="60" width="20" height="10" fill="rgba(0,0,0,0.3)" rx="1" /> {/* Люк */}
  </svg>
);

const QuestionIcon = ({ color, style, className }) => (
  <svg viewBox="0 0 100 100" className={`garage-vehicle ${className}`} style={style}>
     <circle cx="50" cy="50" r="40" fill="currentColor" opacity="0.2" />
     <text x="50" y="70" fontSize="60" fontWeight="900" fill="#fff" textAnchor="middle">?</text>
  </svg>
);

const ICONS = {
  car: CarIcon,
  truck: TruckIcon,
  moto: MotoIcon,
  bus: BusIcon,
  any: QuestionIcon
};

const Garage = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [gameState, setGameState] = useState('loading');
  const [score, setScore] = useState(0);
  const [activeCars, setActiveCars] = useState([]);
  const [questionData, setQuestionData] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [finalData, setFinalData] = useState(null);

  const eventsRef = useRef([]);
  const animationFrameRef = useRef();
  const startTimeRef = useRef(0);
  const gameConfigRef = useRef({});

  // Инициализация
  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      if (data.type === 'update') {
        const state = data.state;
        setScore(state.score || 0);

        if (state.events && state.question) {
            eventsRef.current = state.events.sort((a, b) => a.time - b.time);
            gameConfigRef.current = {
                duration: state.duration,
                question: state.question,
                options: state.options,
                roundId: state.roundId // <--- Сохраняем ID раунда
            };
            startRound();
        }
      }
      else if (data.type === 'next') {
          const fb = data.feedback;

          // МГНОВЕННО обновляем счет, который пришел с сервера
          if (data.state && typeof data.state.score === 'number') {
              setScore(data.state.score);
          }

          setFeedback({
              status: fb.status,
              points: fb.points,
              correct: fb.correct
          });

          if (fb.status === 'success') {
             window.croco?.haptic?.('success');
          } else {
             window.croco?.haptic?.('error');
          }

          setTimeout(() => {
              setFeedback(null);
              // Обновляем остальное состояние для нового раунда
              const state = data.state;
              eventsRef.current = state.events.sort((a, b) => a.time - b.time);
              gameConfigRef.current = {
                  duration: state.duration,
                  question: state.question,
                  options: state.options,
                  roundId: state.roundId // <--- Обновляем ID раунда
              };
              startRound();
          }, 1500);
      }
      else if (data.type === 'game_over') {
          // 1. Если сервер прислал результат последнего хода (feedback) внутри game_over
          if (data.feedback) {
              // Обновляем визуальный счет, если он изменился
              if (data.my && typeof data.my.score === 'number') {
                  setScore(data.my.score);
              }

              // Показываем оверлей успеха или ошибки
              setFeedback(data.feedback);

              if (data.feedback.status === 'success') window.croco?.haptic?.('success');
              else window.croco?.haptic?.('error');

              // Ждем 1.5 секунды, чтобы игрок увидел результат, и только потом показываем финал
              setTimeout(() => {
                  setFeedback(null);
                  setFinalData(data);
                  setGameState('gameover');
              }, 1500);
          }
          // 2. Если фидбека нет (тайм-аут по бездействию) — сразу финал
          else {
              setFinalData(data);
              setGameState('gameover');
          }
      }
    };

    socket.on('arcade:event', handleEvent);

    // ВАЖНО: При размонтировании обязательно чистим анимацию
    return () => {
        socket.off('arcade:event', handleEvent);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [socket]);

  // Запуск раунда
  const startRound = () => {
      // === КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ ===
      // Останавливаем предыдущий цикл, если он был.
      // Это предотвращает ускорение игры в 2 раза.
      if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
      }
      // ==============================

      setGameState('watching');
      setActiveCars([]);
      setQuestionData(null);
      startTimeRef.current = Date.now();
      runSimulation();
  };

  const runSimulation = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;

      while (eventsRef.current.length > 0 && eventsRef.current[0].time <= elapsed) {
          spawnCar(eventsRef.current.shift());
      }

      setActiveCars(prev => prev.filter(c => c.spawnTime + 
        3000 > elapsed));
      // ==============================================

      if (elapsed >= gameConfigRef.current.duration) {
          // Ждем пока все машины доедут (активные очистятся)
          if (activeCars.length === 0) {
             setGameState('question');
             setQuestionData(gameConfigRef.current.question);
             setOptions(gameConfigRef.current.options || [0, 1, 2]);

             // Останавливаем цикл, так как перешли в фазу вопроса
             cancelAnimationFrame(animationFrameRef.current);
             return;
          }
      }

      animationFrameRef.current = requestAnimationFrame(runSimulation);
  };

  const spawnCar = (event) => {
      const id = Math.random().toString(36).substr(2, 9);
      const car = {
          id,
          ...event.car,
          action: event.action,
          gate: event.gate,
          spawnTime: Date.now() - startTimeRef.current
      };
      setActiveCars(prev => [...prev, car]);
      // Очень тихий клик, чтобы не раздражало
      // window.croco?.haptic?.('selection');
  };

  const handleAnswer = (val) => {
      // === НОВОЕ: Отправляем ID раунда ===
      store.ref.work.methods.hostAction('mafiaMiniGameAction', {
          type: 'answer_infinite',
          value: val,
          roundId: gameConfigRef.current.roundId // <--- Защита от дублей
      });
      // Можно также добавить локальную блокировку кнопок здесь, если нужно
      setQuestionData(null); // Скрываем вопрос, чтобы пользователь не нажал дважды визуально
  };

  // --- ИСПРАВЛЕННАЯ ЛОГИКА АНИМАЦИИ ---
  const getCarStyle = (car) => {
      const elapsed = (Date.now() - startTimeRef.current) - car.spawnTime;
      const duration = 2200;

      // БАГФИКС: Ограничиваем прогресс единицей.
      // Это предотвращает "пролет" машины насквозь через центр на противоположную сторону
      let progress = Math.min(1, elapsed / duration);

      // Координаты (в %)
      // Center: 50, 50
      // Gates: дальше за краем экрана, чтобы плавно выезжали
      const gateCoords = [
          { x: 50, y: -20 },  // Top
          { x: 120, y: 50 },  // Right
          { x: 50, y: 120 },  // Bottom
          { x: -20, y: 50 }   // Left
      ];

      const start = car.action === 'enter' ? gateCoords[car.gate] : { x: 50, y: 50 };
      const end = car.action === 'enter' ? { x: 50, y: 50 } : gateCoords[car.gate];

      // Линейная интерполяция
      const currentX = start.x + (end.x - start.x) * progress;
      const currentY = start.y + (end.y - start.y) * progress;

      // Поворот: 0 deg = смотрит вверх (т.к. иконки нарисованы вертикально)
      // Top gate (0): едет вниз -> 180 deg
      // Right gate (1): едет влево -> -90 deg (270)
      // Bottom gate (2): едет вверх -> 0 deg
      // Left gate (3): едет вправо -> 90 deg

      let rotate = 0;
      if (car.gate === 0) rotate = 180;
      if (car.gate === 1) rotate = -90;
      if (car.gate === 2) rotate = 0;
      if (car.gate === 3) rotate = 90;

      // Если это ВЫЕЗД (exit), то машина едет от центра К воротам.
      // Значит, она должна смотреть В сторону ворот.
      // Top gate (0): едет вверх -> 0 deg (разворот на 180 от въезда)
      if (car.action === 'exit') rotate += 180;

      let opacity = 1;

      // Плавное появление
      if (progress < 0.1) opacity = progress * 10;

      // БАГФИКС: Если это въезд (enter) и машина достигла центра (progress=1),
      // скрываем её полностью, чтобы она не "висела" там
      if (car.action === 'enter' && progress >= 0.95) {
          opacity = 0;
      }

      return {
          left: `${currentX}%`,
          top: `${currentY}%`,
          opacity: opacity,
          transform: `translate(-50%, -50%) rotate(${rotate}deg)`
      };
  };

  const renderQuestionBlock = () => {
      if (!questionData) return null;

      const { target, mode } = questionData;

      // Текст вопроса
      let questionText = t('arcade.garage_question_specific');
      if (mode === 'color') questionText = t('arcade.garage_question_color');
      if (mode === 'type') questionText = t('arcade.garage_question_type');

      // Логика отображения иконки
      let iconContent;

      if (mode === 'color') {
          // ЕСЛИ ВОПРОС ПРО ЦВЕТ: Рисуем чистый яркий круг (div), без SVG и вопросов
          // Используем класс цвета, который задает 'color', и наследуем его в background
          iconContent = (
              <div
                  className={`gq-pure-blob car-${target.color}`}
              />
          );
      } else {
          // ЕСЛИ ВОПРОС ПРО ТИП ИЛИ КОНКРЕТНУЮ МАШИНУ
          const Icon = ICONS[target.type] || ICONS.any;
          const colorClass = target.color === 'any' ? 'car-white' : `car-${target.color}`;

          iconContent = (
              <Icon
                  className={colorClass}
                  style={{width: '100%', height: '100%'}}
              />
          );
      }

      return (
          <div className="garage-bottom-panel">
              <div className="gb-question-row">
                  <span className="gb-text">{questionText}</span>

                  <div className="gb-icon-wrapper">
                      {iconContent}
                  </div>

                  <span className="gb-text">?</span>
              </div>

              <div className="gb-options-row">
                  {options.map(opt => (
                      <button key={opt} className="gb-btn" onClick={() => handleAnswer(opt)}>
                          {opt}
                      </button>
                  ))}
              </div>
          </div>
      );
  };

  if (finalData) {
      const { score, rank } = finalData.my;
      return (
        <div className="tetris-gameover">
           <div className="go-title">СЛЕЖКА ОКОНЧЕНА</div>
           <div className="go-rank">{t(`arcade.garage_rank_${rank}`, rank)}</div>
           <div className="go-score">{score} ⭐️</div>
           <UnifiedLeaderboard results={finalData.leaderboard} myId={store.ref.telegram.telegramUserId} t={t} />
           <button className="go-btn" onClick={onClose}>{t('arcade.close')}</button>
        </div>
      );
  }

  return (
    <div className="garage-container">
        {/* Игровая доска (Квадратная) */}
        <div className="garage-board">
            <div className="garage-header">
                <div className="garage-badge">{t('arcade.garage_score')}: {score}</div>
                {gameState === 'watching' && (
                    <div className="garage-badge" style={{color: '#ef4444', borderColor: '#ef4444'}}>● REC</div>
                )}
            </div>

            {/* Дороги */}
            <div className="garage-road road-v" />
            <div className="garage-road road-h" />

            {/* Гараж */}
            <div className="garage-building">
                <div className="garage-gate-marker gate-m-top" />
                <div className="garage-gate-marker gate-m-right" />
                <div className="garage-gate-marker gate-m-bottom" />
                <div className="garage-gate-marker gate-m-left" />
            </div>

            {/* Машины */}
            {activeCars.map(car => {
                const Icon = ICONS[car.type];
                return (
                    <div key={car.id} className="garage-car-wrapper" style={getCarStyle(car)}>
                        <Icon className={`car-${car.color}`} />
                    </div>
                );
            })}

            {/* Фидбек поверх доски */}
            {feedback && (
                <div className="garage-feedback">
                    {feedback.status === 'success' ? (
                        <>
                            <FaCheckCircle className="gf-icon gf-success" />
                            <div className="gf-text">ВЕРНО!</div>
                            <div className="gf-sub">+{feedback.points} XP</div>
                        </>
                    ) : (
                        <>
                            <FaTimesCircle className="gf-icon gf-error" />
                            <div className="gf-text">ОШИБКА</div>
                            <div className="gf-sub">Было: {feedback.correct}</div>
                        </>
                    )}
                </div>
            )}
        </div>

        {/* Нижняя панель (Вопрос и кнопки) - ОТДЕЛЬНО ОТ ДОСКИ */}
        {gameState === 'question' ? renderQuestionBlock() : (
            <div className="garage-bottom-placeholder">
                {t('arcade.garage_description')}
            </div>
        )}
    </div>
  );
};

export default Garage;